"use client"

import { Compass, CreditCard, Gift, LogOut, ShoppingBag, User, UserCircle } from "lucide-react"
import { useTranslations } from "next-intl"
import { usePostHog } from "posthog-js/react"
import { memo, useState } from "react"

import { logout } from "@/app/[locale]/auth/actions"
import NonAuthHeader from "@/components/header/NonAuthHeader"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { CustomerOngoingOrderByStatusLoader } from "@/data/customerDataLoader"
import { useOngoingOrder } from "@/hooks/customer/useOngoingOrder"
import { usePathname, useRouter } from "@/i18n/routing"
import { ROUTES } from "@/routes"

import { LanguageSelector } from "../LanguageSelector"
import PopoverCart from "../PopverCart"
import SpectraLogo from "../shared/SpectraLogo"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog"
import CustomerProfile from "./CustomerProfile"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { useCustomerProfile, useIsMobile } from "@/hooks/useGlobalStore"

const CustomerHeader = memo(function CustomerHeader() {
  const posthog = usePostHog()
  const t = useTranslations("customer")
  const router = useRouter()
  const pathname = usePathname()
  const { customerProfile } = useCustomerProfile()
  const { ongoingOrder } = useOngoingOrder()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const isMobile = useIsMobile()

  const isInCustomerDashboard = pathname.endsWith(ROUTES.customer)

  if (!customerProfile) {
    return <NonAuthHeader />
  }

  return (
    <div className="flex items-center justify-between px-4">
      <div className="ml-1 flex items-baseline gap-4 font-glacialIndifference">
        <SpectraLogo />
      </div>
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="size-4 rounded-full py-4">
              <User className="size-4" />
              <span className="sr-only">{t("header.menu.profile")}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="[&>*]:cursor-pointer">
            {!isInCustomerDashboard && (
              <DropdownMenuItem onClick={() => router.push(ROUTES.customer)}>
                <Compass className="mr-2 size-4" />
                <span>{t("header.menu.explore")}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setIsProfileOpen(true)}>
              <UserCircle className="mr-2 size-4" />
              <span>{t("header.menu.profile")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push(ROUTES.customerWallet)}>
              <CreditCard className="mr-2 size-4" />
              <span>{t("header.menu.wallet")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push(ROUTES.customerOrders())}>
              <ShoppingBag className="mr-2 size-4" />
              <span>{t("header.menu.orders")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push(ROUTES.customerCodes)}>
              <Gift className="mr-2 size-4" />
              <span>{t("header.menu.codes")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className=" text-red-600"
              onClick={() => {
                posthog.reset()
                logout()
              }}
            >
              <LogOut className="mr-2 size-4" />
              <span>{t("header.menu.disconnect")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <PopoverCart ongoingOrder={ongoingOrder as CustomerOngoingOrderByStatusLoader} />
        <LanguageSelector />
        <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
          <DialogContent className="max-w-md">
            <DialogTitle>{t("profile.update.title")}</DialogTitle>
            <VisuallyHidden>
              <DialogDescription>{t("profile.update.description")}</DialogDescription>
            </VisuallyHidden>
            <CustomerProfile customerProfile={customerProfile} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
})

export default CustomerHeader
